<template>
    <AutomationTemplatesLayout class="automation-template-sellsy">
        <template #header>
            <Heading
                :labelTitle="translations.title"
                :showBackButton="this.hasFullAutomationAccess"
                @go-back="$router.push({ name: 'automations.listing' })"
            />
        </template>
        <template #sidebar>
            <CompassInput
                v-model="searchTemplateValue"
                prefixIcon="search"
                :placeholder="$t.__('automation.AutomationTemplatesSellsy.placeholder_search')"
                isEraseable
                class="automation-template-sellsy__search"
            />
            <template v-if="tagsTemplates.length > 0">
                <p
                    class="automation-template-sellsy__tags--title typographies-paragraph-small-semibold"
                >
                    {{ $t.__('common.labels.categories') }}
                </p>
                <div class="automation-template-sellsy__tags">
                    <CompassTagInteractive
                        v-for="(tag, index) in tagsTemplates"
                        :key="tag.category"
                        :modelValue="tag.label"
                        variant="selectable"
                        color="light-gray"
                        :isSelected="tag.selected"
                        :valueLabel="tag.label"
                        @change="handleTagTemplates(index)"
                    />
                </div>
            </template>
            <CompassPlainButton
                v-if="isFiltered"
                :label="$t.__('common.actions.reset')"
                leftIcon="close"
                @click="resetFilters"
            />
        </template>
        <CompassInformationBanner
            :title="translations.featureBanner.title"
            :description="translations.featureBanner.description"
            sourceIllustration="/ressources/logged/img/illustrations/banner-automation.svg"
            class="automation-template-sellsy__banner"
        />
        <div
            v-if="filteredTemplates.length > 0 || isLoadingTemplates"
            class="automation-template-sellsy__container"
        >
            <template v-if="isLoadingTemplates">
                <AutomationTemplateCard
                    v-for="templateLoading in 6"
                    :key="templateLoading"
                    :template="{}"
                    isLoading
                />
            </template>
            <template v-else>
                <AutomationTemplateCard
                    v-for="template in filteredTemplates"
                    :key="template.id"
                    :template="template"
                />
            </template>
        </div>
        <CompassEmptyState
            v-else
            illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
            :caption="translations.captionNoFilterResult"
            class="automation-template-sellsy__empty-state"
        />
    </AutomationTemplatesLayout>
</template>

<script>
import AutomationTemplateCard from 'vcomponents/automation/templates/AutomationTemplateCard.vue';
import AutomationTemplatesLayout from 'vcomponents/automation/templates/AutomationTemplatesLayout.vue';
import Heading from 'vcomponents/common//Heading/Heading.vue';
import AutomationMixin from 'mixins/automation/AutomationMixin.js';

import { getAutomationTemplates } from 'api/automation/automationService.js';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
const { mapState: mapStateUser, mapGetters: mapGettersUser } = createNamespacedHelpers('user');

export default {
    components: {
        AutomationTemplateCard,
        AutomationTemplatesLayout,
        Heading,
    },
    mixins: [AutomationMixin],
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationTemplatesSellsy.title'),
                featureBanner: {
                    title: this.$t.__('automation.AutomationTemplatesSellsy.banner_title'),
                    description: this.$t.__(
                        'automation.AutomationTemplatesSellsy.banner_description',
                    ),
                },
                captionNoFilterResult: this.$t.__('common.labels.caption_no_filter_result'),
            },
            objectCategoryTags: [],
            additionalCategoryTags: [],
            searchTemplateValue: '',
            templates: [],
            isLoadingTemplates: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        ...mapStateUser(['isAdmin']),
        ...mapGettersUser(['getUser']),
        hasFullAutomationAccess() {
            return this.isAdmin || this.getUser.permissions?.acl?.manage_automations;
        },
        isFiltered() {
            return this.searchTemplateValue || this.selectedCategories.length > 0;
        },
        tagsTemplates() {
            const objectTags = Array.isArray(this.objectCategoryTags)
                ? this.objectCategoryTags
                : [];
            const additionalTags = Array.isArray(this.additionalCategoryTags)
                ? this.additionalCategoryTags
                : [];
            return [...objectTags, ...additionalTags];
        },
        filteredTemplates() {
            let filtered = this.templates;

            // Filter by search value
            if (this.searchTemplateValue) {
                const searchValue = this.searchTemplateValue.toLowerCase();
                filtered = filtered.filter((template) => {
                    return (
                        template.title.toLowerCase().includes(searchValue) ||
                        template.description.toLowerCase().includes(searchValue)
                    );
                });
            }

            // Filter by selected categories
            const selectedCategories = this.selectedCategories;
            if (selectedCategories.length > 0) {
                filtered = filtered.filter((template) => {
                    const templateTags = template.tags || [];
                    const templateTagsTitles = templateTags.map(tag => tag.title);
                    return selectedCategories.some((selectedCategory) => {
                        return templateTagsTitles.includes(`category:${selectedCategory}`);
                    });
                });
            }

            return filtered.sort((a, b) => a.title.localeCompare(b.title));
        },
        selectedCategories() {
            const objectTags = Array.isArray(this.objectCategoryTags)
                ? this.objectCategoryTags
                : [];
            const additionalTags = Array.isArray(this.additionalCategoryTags)
                ? this.additionalCategoryTags
                : [];
            const allTags = [...objectTags, ...additionalTags];
            return allTags.filter((tag) => tag.selected).map((tag) => tag.category);
        },
    },
    watch: {
        templates(newTemplates) {
            const internalId = this.$route.params.internalId;
            if (internalId && newTemplates.length > 0) {
                this.openTemplateByInternalId(internalId);
            }
        },
    },
    created() {
        this.getAutomationTemplates();
    },
    methods: {
        getAutomationTemplates() {
            this.isLoadingTemplates = true;
            getAutomationTemplates()
                .then((response) => {
                    const templatesArray = response.data.data;
                    this.templates = templatesArray.map((template) => {
                        const tags = template.tags || [];
                        const tagsTitles = tags.map(tag => tag.title);
                        const category =
                            tagsTitles
                                .find((tag) => tag.startsWith('category:'))
                                ?.replace('category:', '') || '';
                        return {
                            id: template.id,
                            category,
                            title: template.name,
                            description: template.description,
                            version: template.flows[0] || {},
                            isWizard: tagsTitles.includes('wizard'),
                            isPopular: tagsTitles.includes('category:popular'),
                            metadata: template.metadata,
                            tags,
                        };
                    });
                    this.generateTagsTemplates();
                    return this.initializePiecesList(this.templates);
                })
                .catch((error) => {
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingTemplates = false;
                });
        },
        resetFilters() {
            this.searchTemplateValue = '';
            if (Array.isArray(this.objectCategoryTags)) {
                for (const tag of this.objectCategoryTags) {
                    tag.selected = false;
                }
            }
            if (Array.isArray(this.additionalCategoryTags)) {
                for (const tag of this.additionalCategoryTags) {
                    tag.selected = false;
                }
            }
        },
        handleTagTemplates(index) {
            if (
                !Array.isArray(this.objectCategoryTags) ||
                !Array.isArray(this.additionalCategoryTags)
            ) {
                return;
            }
            const objectCategoriesCount = this.objectCategoryTags.length;
            if (index < objectCategoriesCount) {
                this.objectCategoryTags[index].selected = !this.objectCategoryTags[index].selected;
            } else {
                const additionalIndex = index - objectCategoriesCount;
                if (additionalIndex < this.additionalCategoryTags.length) {
                    this.additionalCategoryTags[additionalIndex].selected =
                        !this.additionalCategoryTags[additionalIndex].selected;
                }
            }
        },
        generateTagsTemplates() {
            this.objectCategoryTags = [];
            this.additionalCategoryTags = [];
            const objectCategoryMapping = {
                opportunities: {
                    category: 'opportunities',
                    label: this.$t.__('common.labels.opportunities'),
                },
                tasks: {
                    category: 'tasks',
                    label: this.$t.__('common.labels.tasks'),
                },
                estimate: {
                    category: 'estimate',
                    label: this.$t.__('common.labels.estimates'),
                },
                invoice: {
                    category: 'invoice',
                    label: this.$t.__('common.labels.invoices'),
                },
                prospects: {
                    category: 'prospects',
                    label:
                        this.$t.__('common.labels.prospects').other ||
                        this.$t.__('common.labels.prospects'),
                },
            };
            const additionalCategoryMapping = {
                sales_licence: {
                    category: 'sales_licence',
                    label: this.$t.__('common.labels.sales_licence'),
                },
                billing_licence: {
                    category: 'billing_licence',
                    label: this.$t.__('common.labels.billing_licence'),
                },
                popular: {
                    category: 'popular',
                    label: this.$t.__('common.labels.popular'),
                },
            };

            const allTags = this.templates.flatMap((template) => template.tags || []);
            const availableObjectCategories = new Set();
            const availableAdditionalCategories = new Set();
            for (const tag of allTags) {
                if (tag.title.startsWith('category:')) {
                    const category = tag.title.replace('category:', '');
                    if (objectCategoryMapping[category]) {
                        availableObjectCategories.add(category);
                    } else if (additionalCategoryMapping[category]) {
                        availableAdditionalCategories.add(category);
                    }
                }
            }
            this.objectCategoryTags = Array.from(availableObjectCategories)
                .map((category) => ({
                    category: objectCategoryMapping[category].category,
                    label: objectCategoryMapping[category].label,
                    selected: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));
            this.additionalCategoryTags = Array.from(availableAdditionalCategories)
                .map((category) => ({
                    category: additionalCategoryMapping[category].category,
                    label: additionalCategoryMapping[category].label,
                    selected: false,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        openTemplateByInternalId(internalId) {
            const template = this.templates.find(
                (t) => t.metadata?.template?.internal_id === internalId,
            );
            if (template?.isWizard) {
                this.$store.dispatch('popupPanel/open', {
                    key: 'automationWizardPanel',
                    props: {
                        template: template,
                        readOnly: !this.hasFullAutomationAccess,
                    },
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-template-sellsy {
    &__banner {
        margin: 0 0 var(--spacing-4);
    }

    &__search {
        width: 100%;
        margin: 0 0 var(--spacing-5);
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-1);
        margin: 0 0 var(--spacing-3);

        &--title {
            color: var(--colors-common-text-standard-caption);
            margin: 0 0 var(--spacing-1);
        }
    }

    &__container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(206px, 1fr));
        gap: var(--spacing-3);
        margin: 0 0 var(--spacing-6);
    }

    &__empty-state {
        margin-top: 56px;
    }
}
</style>

<style lang="scss">
.compass-information-banner.automation-template-sellsy__banner {
    background: var(--colors-component-background-information-tag-orange);
}
</style>
